import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import AdminHeader from "../../common/AdminHeader";
import { baseUrl, subjectTypeOptions } from "../../../Utility/constants";
import { fetchSubjectGroupMaster } from "../../../Utility/commonFunctions";
import { SubjectCategoryConfigMasterEndpoint, SubjectCategoryMasterEndpoint } from "../../../Utility/Endpoints";

const SubjectMaster = () => {
  const [subjects, setSubjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [streams, setStreams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [subjectGroupType, setSubjectGroupType] = useState([]);
  const [standardValue, setStandardValue] = useState('');

  const [formData, setFormData] = useState({
    subjectId: 0,
    subjectCode: "",
    subjectName: "",
    subjectMedium: "",
    subjectGroup: null, // <-- nullable
    subjectType: [],
    subjectCategory: "",
    subjectCategoryConfigId: "",
    classId: "",
    streamId: null, // <-- nullable
    isGrade: false,
    createdBy: localStorage.getItem("userID"),
    updatedBy: localStorage.getItem("userID"),
    capacity: 0, // <-- nullable
  });

  const [editing, setEditing] = useState(false);

  const [dropdowns, setDropdowns] = useState({
    subjectMediumOptions: [],
  });

  const fetchCategories = async () => {
    try {
      const response = await axios.get(SubjectCategoryMasterEndpoint);
      setCategories(response.data);
    } catch (error) {
      toast.error("Failed to fetch categories");
    }
  };

  const fetchConfigs = async (subjectCategoryId) => {
    try {
      const response = await axios.get(
        `${SubjectCategoryConfigMasterEndpoint}/GetByCategoryId?id=${subjectCategoryId}`
      );
      setConfigs(response.data);
    } catch (error) {
      toast.error("Failed to fetch configurations");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchDropdowns();
    fetchStreams();
    fetchClasses();
    fetchSubjects();
  }, []);

  useEffect(() => {
    const loadSubjectGroups = async () => {
      const subjectGroupOptions = await fetchSubjectGroupMaster();
      setSubjectGroupType(subjectGroupOptions);
    };
    loadSubjectGroups();
  }, []);

  const fetchDropdowns = async () => {
    try {
      const schoolMediumRes = await axios.get(
        `${baseUrl}/api/CommonMaster/GetSchoolMediumMasters`
      );
      setDropdowns({
        subjectMediumOptions: schoolMediumRes.data,
      });
    } catch (error) {
      console.log("Failed to fetch dropdown data");
    }
  };

  const fetchStreams = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/StreamMaster`);
      const activeStreams = response.data.filter((stream) => stream.isActive === 1);
      setStreams(activeStreams);
    } catch (error) {
      toast.error("Failed to fetch streams");
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/StandardMaster/GetStandardMastersByAdmin?AdminId=${localStorage.getItem(
          "userID"
        )}`
      );
      setClasses(response.data);
    } catch (error) {
      toast.error("Failed to fetch classes");
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/SubjectMaster`);
      setSubjects(response.data);
    } catch (error) {
      toast.error("Failed to fetch subjects");
    }
  };

  // Handle Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Convert empty strings to null for nullable fields
    let newValue = value === "" && (name === "subjectGroup" || name === "streamId") ? null : value;

    if (name === "subjectCategory" && value !== "") {
      fetchConfigs(value);
    } else if (name === "subjectCategory" && value === "") {
      setConfigs([]);
    }

    if (name === 'classId') {
      setStandardValue(e.target.options[e.target.selectedIndex].text);
    }

    setFormData({ ...formData, [name]: newValue });
  };

  // Handle Checkbox Change
  const handleCheckboxChange = (value) => {
    const updatedTypes = formData.subjectType.includes(value)
      ? formData.subjectType.filter((type) => type !== value)
      : [...formData.subjectType, value];
    setFormData({ ...formData, subjectType: updatedTypes });
  };

  const handleOpenModal = async (subject) => {
    if (subject?.subjectId) {
      try {
        const response = await axios.get(`${baseUrl}/api/SubjectMaster/${subject.subjectId}`);
        if (response && response.data) {
          fetchConfigs(response.data.subjectCategory);
          const apiSubject = response.data;
          setEditing(true);
          setFormData({
            subjectId: apiSubject.subjectId,
            subjectCode: apiSubject.subjectCode || "",
            subjectName: apiSubject.subjectName || "",
            subjectMedium: apiSubject.subjectMedium || "",
            subjectGroup: apiSubject.subjectGroup || null,
            isGrade: apiSubject.isGrade || false,
            subjectType: apiSubject.subjectType
              ? apiSubject.subjectType.split(",").map((s) => s.trim())
              : [],
            subjectCategory: apiSubject.subjectCategory || "",
            subjectCategoryConfigId: apiSubject.subjectCategoryConfigId || "",
            classId: apiSubject.classId || "",
            streamId: apiSubject.streamId || null,
            createdBy: apiSubject.createdBy || localStorage.getItem("userID"),
            updatedBy: localStorage.getItem("userID"),
            capacity : apiSubject.capacity || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching subject:", error);
        toast.error("Failed to fetch subject details");
      }
    } else {
      setEditing(false);
      setFormData({
        subjectId: 0,
        subjectCode: "",
        subjectName: "",
        subjectMedium: "",
        subjectGroup: null,
        isGrade: false,
        subjectType: [],
        subjectCategory: "",
        subjectCategoryConfigId: "",
        classId: "",
        streamId: null,
        createdBy: localStorage.getItem("userID"),
        updatedBy: localStorage.getItem("userID"),
      });
    }

    setShowModal(true);
  };

  const handleSave = async () => {
    const payload = {
      ...formData,
      subjectType: formData.subjectType.join(","), // Convert to comma-separated string
    };
    try {
      if (editing) {
        await axios.put(`${baseUrl}/api/SubjectMaster/${formData.subjectId}`, payload);
        toast.success("Subject updated successfully");
      } else {
        await axios.post(`${baseUrl}/api/SubjectMaster`, payload);
        toast.success("Subject added successfully");
      }
      fetchSubjects();
      setShowModal(false);
    } catch (error) {
      toast.error("Failed to save subject");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this subject?")) {
      try {
        await axios.delete(`${baseUrl}/api/SubjectMaster/${id}`);
        toast.success("Subject deleted successfully");
        fetchSubjects();
      } catch (error) {
        toast.error("Failed to delete subject");
      }
    }
  };

  return (
    <>
      <AdminHeader />
      <div className="container mt-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Subject Master</h2>
          <Button variant="primary" onClick={() => handleOpenModal(null)}>
            Add New
          </Button>
        </div>
        <table className="table table-bordered table-striped">
          <thead className="thead-dark">
            <tr>
              <th>Subject Code</th>
              <th>Subject Name</th>
              <th>Standard</th>
              <th>Group</th>
              <th>Stream</th>
              <th>Medium</th>
              <th>Type</th>
              <th>Category</th>
              <th>Config</th>
              <th>Limit</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((subject) => (
              <tr key={subject.subjectId}>
                <td>{subject.subjectCode}</td>
                <td>{subject.subjectName}</td>
                <td>{subject.standardName}</td>
                <td>{subject.subjectGroup}</td>
                <td>{subject.streamName}</td>
                <td>{subject.subjectMedium}</td>
                <td>{subject.subjectType}</td>
                <td>{subject.subjectCategoryName}</td>
                <td>{subject.subjectCategoryConfigName}</td>
                <td>{subject.capacity}</td>
                <td>
                  <Button variant="warning" onClick={() => handleOpenModal(subject)}>
                    Edit
                  </Button>{" "}
                  <Button variant="danger" onClick={() => handleDelete(subject.subjectId)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Edit Subject" : "Add Subject"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Subject Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="subjectCode"
                    value={formData.subjectCode}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Subject Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="subjectName"
                    value={formData.subjectName}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Standard</Form.Label>
                  <Form.Select name="classId" value={formData.classId} onChange={handleChange}>
                    <option value="">Select Class</option>
                    {classes.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.standardName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Group</Form.Label>
                  <Form.Select
                    name="subjectGroup"
                    value={formData.subjectGroup ?? ""}
                    onChange={handleChange}
                    disabled={standardValue && Number(standardValue) <= 10}
                  >
                    <option value="">Select Group</option>
                    {subjectGroupType.map((option) => (
                      <option key={option.subjectGroupId} value={option.subjectGroupId}>
                        {option.subjectGroupName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Stream</Form.Label>
                  <Form.Select
                    name="streamId"
                    value={formData.streamId ?? ""}
                    onChange={handleChange}
                    disabled={standardValue && Number(standardValue) <= 10}
                  >
                    <option value="">Select Stream</option>
                    {streams.map((stream) => (
                      <option key={stream.id} value={stream.id}>
                        {stream.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Medium</Form.Label>
                  <Form.Select name="subjectMedium" value={formData.subjectMedium} onChange={handleChange}>
                    <option value="">Select Medium</option>
                    {dropdowns.subjectMediumOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <div className="d-flex flex-wrap">
                    {subjectTypeOptions.map((option) => (
                      <Form.Check
                        key={option.id}
                        type="checkbox"
                        label={option.value}
                        checked={formData.subjectType.includes(option.value)}
                        onChange={() => handleCheckboxChange(option.value)}
                        className="me-3"
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Select name="subjectCategory" value={formData.subjectCategory} onChange={handleChange}>
                    <option value="">Select Category</option>
                    {categories.map((option) => (
                      <option key={option.subjectCategoryID} value={option.subjectCategoryID}>
                        {option.subjectCategoryName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Category Config</Form.Label>
                  <Form.Select
                    name="subjectCategoryConfigId"
                    value={formData.subjectCategoryConfigId}
                    onChange={handleChange}
                  >
                    <option value="">Select Category Config</option>
                    {configs.map((option) => (
                      <option key={option.subjectCategoryConfigID} value={option.subjectCategoryConfigID}>
                        {option.subjectCategoryConfigName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

             
              <Col md={6}>
                <Form.Group controlId="isGrade">                 
                  <Form.Label>Capacity</Form.Label>
                  <Form.Control
                    type="text"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
               <Col md={6}>
                <Form.Group controlId="isGrade">
                  <br />
                  <Form.Check
                    type="checkbox"
                    label="Is Grade Subject"
                    checked={formData.isGrade}
                    onChange={(e) =>
                      setFormData({ ...formData, isGrade: e.target.checked ? true : false })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </>
  );
};

export default SubjectMaster;
